import React from "react";
import { SkeletonBlock } from "./Skeleton";

/**
 * Tailored Portfolio skeleton (Open Orders / Holdings / Trade History).
 *
 * Two pieces, used independently:
 *
 *   PortfolioSummarySkeleton — the Free Cash / Portfolio strip. The strip's
 *     chrome and its two labels are real and instant; only the two figures are
 *     grey. This is the one place a placeholder would do real damage: a "$0.00"
 *     here reads as "your account is empty".
 *
 *   PortfolioRowsSkeleton — the position/order cards. Row geometry matches the
 *     real cards (44px asset chip, three stacked text lines, right-aligned
 *     value) so nothing moves when the rows land.
 *
 * No directional colour anywhere: green/red on a portfolio row means gain or
 * loss, and we don't know yet.
 */

export const PortfolioSummarySkeleton = () => (
  <div
    className="rounded-xl mb-6 p-4 md:px-8 md:py-5 md:mb-6 flex items-stretch border border-[#BFC8C8]/30 shadow-sm"
    style={{ backgroundColor: "rgba(53,178,60,0.03)" }}
    aria-busy="true"
    aria-label="Account summary loading"
  >
    <div className="flex-1 pr-3 md:pr-8">
      <p className="text-[10px] md:text-[11px] font-semibold uppercase tracking-wide text-[#6F7978]">
        Free Cash
      </p>
      <SkeletonBlock height={24} width={124} className="mt-1.5" />
    </div>
    <div
      className="w-px self-stretch my-1"
      style={{ backgroundColor: "rgba(0,76,80,0.05)" }}
    />
    <div className="flex-1 pl-3 md:pl-8 flex flex-col items-end">
      <p className="text-[10px] md:text-[11px] font-semibold uppercase tracking-wide text-[#6F7978]">
        Portfolio
      </p>
      <SkeletonBlock height={24} width={124} className="mt-1.5" />
    </div>
  </div>
);

const PortfolioRow = () => (
  <div className="bg-white py-4 border-b border-gray-200 md:mb-3 md:rounded-xl md:border md:border-gray-200 md:p-5">
    <div className="flex items-start">
      {/* Same 44px chip the real rows use. */}
      <SkeletonBlock
        rounded="rounded-full"
        width={44}
        height={44}
        className="shrink-0"
      />
      <div className="flex-1 ml-3 md:ml-4 min-w-0">
        <div className="flex items-start">
          <div className="flex-1 min-w-0 pr-3">
            <SkeletonBlock height={16} width="58%" />
            <SkeletonBlock height={12} width="34%" className="mt-2" />
            <SkeletonBlock height={14} width="46%" className="mt-2.5" />
          </div>
          <SkeletonBlock height={14} width={78} className="shrink-0" />
        </div>
      </div>
    </div>
  </div>
);

/** `columnLabels` renders the real column header above the rows, if the tab has one. */
export const PortfolioRowsSkeleton = ({ rows = 4, valueLabel }) => (
  <div aria-busy="true" aria-label="Positions loading">
    {valueLabel && (
      <div className="flex items-center justify-between px-4 md:px-5 pb-2 mb-[18px] border-b border-[#E0E6ED]">
        <span className="text-[10px] md:text-[11px] font-semibold uppercase tracking-wide text-[#6F7978] ml-[56px] md:ml-[60px]">
          Asset
        </span>
        <span className="text-[10px] md:text-[11px] font-semibold uppercase tracking-wide text-[#6F7978]">
          {valueLabel}
        </span>
      </div>
    )}
    {Array.from({ length: rows }).map((_, i) => (
      <PortfolioRow key={i} />
    ))}
  </div>
);

export default PortfolioRowsSkeleton;
